import React, { FC } from 'react'
import { WebinarRecordingBodyContainer } from '../style'

//* utils
import { ModuleNames } from '../../../../../../constants/componentNames.constants'
import { LocaleCodes } from '../../../../../../constants/localization.constants'
import { findTarget } from '../../../../../../utils/microcopy.utils'
import { renderRichText } from '../../../../../../utils/richTextHandler.utils'
import formatInlineCode from '../../../../../../utils/shortTextFormatting'

//* components
import Button from '../../../../../global/button'
import ContentBlock from '../../../../../global/contentBlock'
import GlobalContainer from '../../../../../global/globalContainer'
import MainCTA from '../../../../../global/mainCta'
import DynamicRow from '../../../../../global/relatedResources'
import ResourceRow from '../../../../../global/resourceRow'
import SEOTags from '../../../../../global/seoTags'
import Typography from '../../../../../global/typography'
import { RenderHeroSpotlight, RenderSpeakers } from './render'

type BodyProps = {
  webinarRecordingData: any
  locale: string
}

type PageProps = {
  webinarRecordingData: any
  relatedResources: any
  path: string
  locale: string
  availableLocales: any
  location: any
}

export const PageBody: FC<BodyProps> = ({ webinarRecordingData, locale }) => {
  const countryCode = locale.slice(3)
  const englishSpeakerText = `resource.${webinarRecordingData.number}.speakers.text`
  const globalSpeakerText = `resource.${countryCode}.${webinarRecordingData.number}.speakers.text`

  const speakerText = findTarget(
    locale === LocaleCodes.ENGLISH.code ? englishSpeakerText : globalSpeakerText,
    webinarRecordingData.microcopy
  )

  return (
    <GlobalContainer className="WebinarRecording__Outer-Container">
      <WebinarRecordingBodyContainer>
        <div className="WebinarRecording__Body__left-container">
          {webinarRecordingData.rtfBody && (
            <div className="WebinarRecording__Body">
              {renderRichText(webinarRecordingData.rtfBody)}
            </div>
          )}

          {webinarRecordingData.finePrint && (
            <div className="WebinarRecording__fineprint">
              {renderRichText(webinarRecordingData.finePrint)}
            </div>
          )}
        </div>

        <div className="WebinarRecording__Body__right-container">
          {webinarRecordingData.speakers?.length > 0 && (
            <div className="WebinarRecording__Speakers-container">
              <RenderSpeakers
                webinarRecordingData={webinarRecordingData}
                speakerText={speakerText}
              />
            </div>
          )}

          {webinarRecordingData.faqText && (
            <>
              <Typography className="WebinarRecording__FAQ-text font__inter" type="body">
                {formatInlineCode(webinarRecordingData.faqText)}
              </Typography>

              {webinarRecordingData.faqCta?.externalUrl && (
                <Button
                  type="text-link"
                  theme="light"
                  text={webinarRecordingData.faqCta.text}
                  icon={false}
                  href={webinarRecordingData.faqCta.externalUrl}
                />
              )}
            </>
          )}
        </div>
      </WebinarRecordingBodyContainer>
    </GlobalContainer>
  )
}

export const PageInner: FC<PageProps> = ({
  webinarRecordingData,
  path,
  locale,
  relatedResources,
  availableLocales,
  location,
}) => {
  const countryCode = locale.slice(3)
  const englishRecordingRow = `resource.${webinarRecordingData.number}.RecordingROW.text`
  const globalRecordingRow = `resource.${countryCode}.${webinarRecordingData.number}.RecordingROW.text`

  const dynamicRow = (
    <DynamicRow
      resourceRowIntro={findTarget(
        locale === LocaleCodes.ENGLISH.code ? englishRecordingRow : globalRecordingRow,
        webinarRecordingData.microcopy
      )}
      relatedResources={relatedResources || []}
      location={location}
      locale={locale}
    />
  )

  let displayDynamicRow: boolean = true
  const dynamicPageContent =
    webinarRecordingData.flexSection.map((module: any, i: number) => {
      switch (module.__typename) {
        case ModuleNames.GLOBAL_CONTENT_BLOCK:
          return <ContentBlock data={module} key={`page-component--${i}`} />

        case ModuleNames.RESOURCE_ROW:
          displayDynamicRow = false
          return <ResourceRow data={module} key={`page-component--${i}`} locale={locale} />

        case ModuleNames.GLOBAL_MAIN_CTA:
          return <MainCTA data={module} key={`page-component--${i}`} />
      }
    }) || []

  return (
    <div data-cy={`WebinarRecording`}>
      <SEOTags
        seoMetadata={webinarRecordingData.seoMetadata}
        slug={webinarRecordingData.slug}
        path={path}
        locale={locale}
        siteSection="Resource"
        availableLocales={availableLocales}
      />

      <RenderHeroSpotlight
        webinarRecordingData={webinarRecordingData}
        locale={locale}
        location={location}
      />

      <PageBody webinarRecordingData={webinarRecordingData} locale={locale} />
      {displayDynamicRow ? dynamicRow : null}
      {dynamicPageContent}
    </div>
  )
}
